import { ColumnType } from 'antd/es/table'
import { FilterDropdownProps } from 'antd/es/table/interface'
import { Space, Tooltip, Tag, Divider, Button, Select, Image } from 'antd'
import { BasSwitch, IconFont } from '@/components'
import styled from 'styled-components'
import {
  resourceLevelMap,
  riskLevelMap,
  templateTypeOptions,
  riskLevelOptions,
  resourceLevelOptions,
  tableColumnWidths,
} from '../consts'
import leakSeverity from '@/assets/imges/leak-severity.png'
import leakCenter from '@/assets/imges/leak-center.png'
import leakLow from '@/assets/imges/leak-low.png'
import leakHigh from '@/assets/imges/leak-high.png'
import type { TemplateItem, CaseItem } from '../interface'
import { t } from '@/i18n'

// 风险等级图标映射
const riskLevelIconMap = {
  RiskLevelLow: <Image src={leakLow} width={19} />,
  RiskLevelMiddle: <Image src={leakCenter} width={19} />,
  RiskLevelHigh: <Image src={leakHigh} width={19} />,
  RiskLevelGross: <Image src={leakSeverity} width={19} />,
}

// 模板列表列定义
export const createTemplateColumns = (
  searchTemplateParams: any,
  templateSelectedCountMap: Record<string, number>,
  selectedCaseIds: string[],
  formState: any,
  setTemplateTypeSelectOpen: (open: boolean) => void,
  templateTypeSelectOpen: boolean,
  handleTemplateSwitchChange: (templateId: string, checked: boolean) => void,
): ColumnType<TemplateItem>[] => [
  {
    title: t('模板名称'),
    dataIndex: 'name',
    key: 'name',
    render: text => (
      <Space>
        <Tooltip title={text}>
          <span
            style={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
          >
            {text}
          </span>
        </Tooltip>
      </Space>
    ),
  },
  {
    title: t('模板类型'),
    dataIndex: 'originType',
    key: 'originType',
    filteredValue: searchTemplateParams?.filterParams?.originType
      ? searchTemplateParams?.filterParams?.originType
      : '',
    filterDropdown: (props: FilterDropdownProps) => (
      <div style={{ padding: 8, width: 300 }}>
        <Select
          mode="multiple"
          allowClear
          maxTagCount={'responsive'}
          style={{ width: '100%' }}
          placeholder={t('请选择模板类型')}
          value={props.selectedKeys as string[]}
          open={templateTypeSelectOpen}
          getPopupContainer={() => document.body as HTMLElement}
          popupClassName="table-filter-dropdown"
          onDropdownVisibleChange={setTemplateTypeSelectOpen}
          onChange={value => {
            props.setSelectedKeys(value)
          }}
          dropdownRender={menu => (
            <div style={{ marginBottom: '6px' }}>
              {menu}
              <Divider style={{ margin: '3px 0 6px 0' }} />
              <Space className="space-btn" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="default"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.clearFilters?.()
                  }}
                >
                  {t('重置')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.confirm()
                    setTemplateTypeSelectOpen(false)
                  }}
                >
                  {t('确定')}
                </Button>
              </Space>
            </div>
          )}
          options={templateTypeOptions}
        />
      </div>
    ),

    filterIcon: (filtered: boolean) => (
      <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
        <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
      </Tooltip>
    ),

    render: (originType: string) => (
      <Tag color={originType === 'OriginTypeSystem' ? '#1f8fa7' : '#f77b83'}>
        {originType === 'OriginTypeSystem' ? t('系统内置') : t('手动添加')}
      </Tag>
    ),
  },
  {
    title: t('执行更新'),
    dataIndex: 'executeUpdate',
    key: 'executeUpdate',
    width: tableColumnWidths.executeUpdate,
    render: (_, record) => {
      // 检查该模板下的用例是否全部选中
      const selectedCount = templateSelectedCountMap[record.templateId] || 0
      const isAllCasesSelected =
        selectedCount === record.resourceAmount && record.resourceAmount > 0

      // 当模板增量更新关闭时，执行更新开关应该显示为关闭状态且不可操作
      const isChecked = formState.executeTemplateLatestCase ? record.executeUpdate : false
      // 只有当第一步的模板增量更新开关打开，且是周期任务，且该模板下的全部用例和已选用例数相等时，开关才能操作
      const isDisabled =
        formState.executeType !== 3 || !formState.executeTemplateLatestCase || !isAllCasesSelected

      return (
        <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
          <BasSwitch
            checked={isChecked}
            disabled={isDisabled}
            onChange={(checked, e) => {
              e.stopPropagation()
              if (isDisabled) return
              handleTemplateSwitchChange(record.templateId, checked)
            }}
          />
        </div>
      )
    },
  },
  {
    title: t('全部|已选用例数'),
    dataIndex: 'TemplateSortFieldResourceAmount',
    key: 'TemplateSortFieldResourceAmount',
    sorter: (a: TemplateItem, b: TemplateItem) => a.resourceAmount - b.resourceAmount,
    render: (_, record: TemplateItem) => {
      const selectedCount = templateSelectedCountMap[record.templateId] || 0
      return `${record.resourceAmount} | ${selectedCount}`
    },
  },
  {
    title: t('更新时间'),
    dataIndex: 'TemplateSortFieldUpdateTime',
    key: 'TemplateSortFieldUpdateTime',
    sorter: (a: TemplateItem, b: TemplateItem) =>
      new Date(a.updateTime).getTime() - new Date(b.updateTime).getTime(),
    render: (_, record: TemplateItem) => (
      <span>{record.updateTime?.split('T')[0] ? record.updateTime.split('T')[0] : '-'}</span>
    ),
  },
]

// 已选用例列表列定义
export const createSelectedCaseColumns = (
  searchCaseParams: any,
  attackTypeList: any[],
  setRiskLevelSelectOpen: (open: boolean) => void,
  setResourceLevelSelectOpen: (open: boolean) => void,
  setAttackTypeSelectOpen: (open: boolean) => void,
  riskLevelSelectOpen: boolean,
  resourceLevelSelectOpen: boolean,
  attackTypeSelectOpen: boolean,
  setDetailVisible: (visible: { visible: boolean; resourceId: string }) => void,
): ColumnType<CaseItem>[] => [
  {
    title: t('用例名称'),
    dataIndex: 'name',
    key: 'name',
    width: tableColumnWidths.caseName,
    render: (text: string) => (
      <Space>
        <Tooltip title={text}>
          <span
            style={{
              maxWidth: 150,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
          >
            {text}
          </span>
        </Tooltip>
      </Space>
    ),
  },
  {
    title: t('危险等级'),
    dataIndex: 'riskLevel',
    key: 'riskLevel',
    width: tableColumnWidths.riskLevel,
    filteredValue: searchCaseParams?.filterParams?.riskLevel
      ? searchCaseParams?.filterParams?.riskLevel
      : '',
    filterDropdown: (props: FilterDropdownProps) => (
      <div style={{ padding: 8, width: 300 }}>
        <Select
          mode="multiple"
          allowClear
          maxTagCount={'responsive'}
          style={{ width: '100%' }}
          placeholder={t('请选择危险等级')}
          value={props.selectedKeys as string[]}
          open={riskLevelSelectOpen}
          getPopupContainer={() => document.body as HTMLElement}
          popupClassName="table-filter-dropdown"
          onDropdownVisibleChange={setRiskLevelSelectOpen}
          onChange={value => {
            props.setSelectedKeys(value)
          }}
          dropdownRender={menu => (
            <div style={{ marginBottom: '6px' }}>
              {menu}
              <Divider style={{ margin: '3px 0 6px 0' }} />
              <Space className="space-btn" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="default"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.clearFilters?.()
                  }}
                >
                  {t('重置')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.confirm()
                    setRiskLevelSelectOpen(false)
                  }}
                >
                  {t('确定')}
                </Button>
              </Space>
            </div>
          )}
          options={riskLevelOptions}
        />
      </div>
    ),

    filterIcon: (filtered: boolean) => (
      <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
        <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
      </Tooltip>
    ),

    render: (text: string) => (
      <div>
        {riskLevelIconMap[text]}
        <span className={riskLevelMap[text].class}>{riskLevelMap[text].label}</span>
      </div>
    ),
  },
  {
    title: t('用例分级'),
    dataIndex: 'resourceLevel',
    key: 'resourceLevel',
    width: tableColumnWidths.resourceLevel,
    filteredValue: searchCaseParams?.filterParams?.resourceLevel
      ? searchCaseParams?.filterParams?.resourceLevel
      : '',
    filterDropdown: (props: FilterDropdownProps) => (
      <div style={{ padding: 8, width: 300 }}>
        <Select
          mode="multiple"
          allowClear
          maxTagCount={'responsive'}
          style={{ width: '100%' }}
          placeholder={t('请选择用例分级')}
          value={props.selectedKeys as string[]}
          open={resourceLevelSelectOpen}
          getPopupContainer={() => document.body as HTMLElement}
          popupClassName="table-filter-dropdown"
          onDropdownVisibleChange={setResourceLevelSelectOpen}
          onChange={value => {
            props.setSelectedKeys(value)
          }}
          dropdownRender={menu => (
            <div style={{ marginBottom: '6px' }}>
              {menu}
              <Divider style={{ margin: '3px 0 6px 0' }} />
              <Space className="space-btn" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="default"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.clearFilters?.()
                  }}
                >
                  {t('重置')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.confirm()
                    setResourceLevelSelectOpen(false)
                  }}
                >
                  {t('确定')}
                </Button>
              </Space>
            </div>
          )}
          options={resourceLevelOptions}
        />
      </div>
    ),

    filterIcon: (filtered: boolean) => (
      <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
        <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
      </Tooltip>
    ),

    render: (level: string) => (
      <span className={resourceLevelMap[level].class}>{resourceLevelMap[level].label}</span>
    ),
  },
  {
    title: t('攻击类型'),
    dataIndex: 'attackTypeId',
    key: 'attackTypeId',
    width: tableColumnWidths.attackType,
    filteredValue: searchCaseParams?.filterParams?.attackTypeId
      ? searchCaseParams?.filterParams?.attackTypeId
      : '',
    filterDropdown: (props: FilterDropdownProps) => (
      <div style={{ padding: 8, width: 300 }}>
        <Select
          allowClear
          mode="multiple"
          maxTagCount={'responsive'}
          style={{ width: '100%' }}
          filterOption={(input, option) => {
            const attackTypeName =
              attackTypeList?.find(item => item.id === option?.value)?.name || ''
            return attackTypeName.toLowerCase().includes(input.toLowerCase())
          }}
          placeholder={t('请选择攻击类型')}
          value={props.selectedKeys as string[]}
          open={attackTypeSelectOpen}
          getPopupContainer={() => document.body as HTMLElement}
          popupClassName="table-filter-dropdown"
          onDropdownVisibleChange={setAttackTypeSelectOpen}
          onChange={value => {
            props.setSelectedKeys(value)
          }}
          dropdownRender={menu => (
            <div style={{ marginBottom: '6px' }}>
              {menu}
              <Divider style={{ margin: '3px 0 6px 0' }} />
              <Space className="space-btn" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="default"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.clearFilters?.()
                  }}
                >
                  {t('重置')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  style={{ letterSpacing: '-2px', padding: '0 12px' }}
                  onClick={() => {
                    props.confirm()
                    setAttackTypeSelectOpen(false)
                  }}
                >
                  {t('确定')}
                </Button>
              </Space>
            </div>
          )}
          options={attackTypeList?.map(item => ({
            label: item.name,
            value: item.id,
          }))}
        />
      </div>
    ),

    filterIcon: (filtered: boolean) => (
      <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
        <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
      </Tooltip>
    ),

    render: (_, record: CaseItem) => {
      const attackTypeId = record.attackTypeId
      const attackType = attackTypeList.find(item => String(item.id) === String(attackTypeId))
      return attackType?.name || '-'
    },
  },
  {
    title: t('更新时间'),
    dataIndex: 'ResourceSortFieldUpdateTime',
    key: 'ResourceSortFieldUpdateTime',
    width: tableColumnWidths.updateTime,
    sorter: (a: CaseItem, b: CaseItem) =>
      new Date(a.updateTime).getTime() - new Date(b.updateTime).getTime(),
    render: (_, record: CaseItem) => (
      <span>{record.updateTime?.split('T')[0] ? record.updateTime.split('T')[0] : '-'}</span>
    ),
  },
  {
    title: t('操作'),
    key: 'action',
    width: tableColumnWidths.action,
    render: (_, record) => (
      <span
        onClick={() => {
          setDetailVisible({ visible: true, resourceId: String(record.resourceId) })
        }}
        className="detail-btn"
      >
        {t('详情')}
      </span>
    ),
  },
]

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`
